import { FormEvent, useState } from "react";
import { runtimeConfig } from "./lib/runtime-config";

export type PublicNewsletterSettings = {
  enabled: boolean;
  title?: string;
  description?: string;
  consent_label?: string;
  privacy_url?: string;
};
const endpoint = `${runtimeConfig.supabaseUrl.replace(/\/$/, "")}/functions/v1/public-newsletter`;
const headers = {
  "content-type": "application/json",
  apikey: runtimeConfig.supabasePublishableKey,
};

export function PublicNewsletter({
  slug,
  settings,
}: {
  slug: string;
  settings?: PublicNewsletterSettings;
}) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{
    text: string;
    error: boolean;
    token?: string;
  } | null>(null);
  if (!settings?.enabled) return null;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setNotice(null);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          stand_slug: slug,
          email,
          consent,
          source_label: "Site público · Newsletter",
          website: String(form.get("website") ?? ""),
        }),
      });
      const body = (await response.json()) as {
        error?: string;
        unsubscribe_token?: string;
      };
      if (!response.ok)
        throw new Error(body.error || "Não foi possível subscrever.");
      setEmail("");
      setConsent(false);
      setNotice({
        text: "Subscrição concluída. Guarde a ligação de cancelamento até receber a primeira comunicação.",
        error: false,
        token: body.unsubscribe_token,
      });
    } catch (error) {
      setNotice({
        text:
          error instanceof Error
            ? error.message
            : "Não foi possível subscrever.",
        error: true,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="theme-newsletter" aria-label="Newsletter">
      <div>
        <p>NOVIDADES</p>
        <h2>{settings.title}</h2>
        <span>{settings.description}</span>
      </div>
      <form onSubmit={submit}>
        <div className="theme-newsletter-input">
          <label htmlFor={`newsletter-${slug}`}>Email</label>
          <input
            id={`newsletter-${slug}`}
            type="email"
            required
            maxLength={254}
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="O seu melhor email"
          />
          <button className="theme-primary" disabled={busy}>
            {busy ? "A subscrever…" : "Subscrever"}
          </button>
        </div>
        <label className="theme-newsletter-consent">
          <input
            type="checkbox"
            required
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
          />
          <span>
            {settings.consent_label}{" "}
            {settings.privacy_url && (
              <a href={settings.privacy_url}>Consultar política</a>
            )}
          </span>
        </label>
        <input
          name="website"
          className="theme-newsletter-honeypot"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        {notice && (
          <p className={notice.error ? "error" : "success"} role="status">
            {notice.text}
            {notice.token && (
              <>
                {" "}
                <a
                  href={`/newsletter/cancelar?token=${encodeURIComponent(notice.token)}`}
                >
                  Cancelar subscrição
                </a>
              </>
            )}
          </p>
        )}
      </form>
    </section>
  );
}

export function PublicNewsletterUnsubscribe() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [busy, setBusy] = useState(false),
    [notice, setNotice] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          action: "unsubscribe",
          token,
          reason: String(form.get("reason") ?? ""),
        }),
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok)
        throw new Error(body.error || "Não foi possível cancelar.");
      setNotice(
        "Subscrição cancelada. Não receberá novas comunicações desta newsletter.",
      );
    } catch (error) {
      setNotice(
        error instanceof Error ? error.message : "Não foi possível cancelar.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="stand-public-state">
      <form className="theme-newsletter-unsubscribe" onSubmit={submit}>
        <h1>Cancelar newsletter</h1>
        <p>
          O cancelamento é imediato e fica registado. Pode voltar a subscrever
          no futuro.
        </p>
        <label>
          <span>Motivo (opcional)</span>
          <textarea name="reason" maxLength={300} />
        </label>
        <button className="theme-primary" disabled={busy || !token}>
          {busy ? "A cancelar…" : "Cancelar subscrição"}
        </button>
        {!token && <p>Ligação de cancelamento inválida.</p>}
        {notice && <p role="status">{notice}</p>}
      </form>
    </main>
  );
}
